import * as Sentry from '@sentry/vue';
import { App } from 'vue';
import { Store } from 'vuex';
import { SENTRY_DSN } from '@/utils/const';

/** The deploy an event came from: a local serve, the dev. site, or production. */
const getEnvironment = (): string => {
  if (process.env.NODE_ENV !== 'production') return 'development';
  return window.location.hostname.startsWith('dev.') ? 'dev' : 'production';
};

/**
 * Starts Sentry for the whole app. From here on the SDK reports component
 * errors, uncaught exceptions and unhandled promise rejections on its own.
 */
export const initSentry = (app: App, store: Store<any>) => {
  Sentry.init({
    app,
    dsn: SENTRY_DSN,
    environment: getEnvironment(),
    // Setting this option to true will send default PII data to Sentry.
    // For example, automatic IP address collection on events
    sendDefaultPii: true,
  });

  // Attach the signed-in admin to every event, with the same fields the backend
  // reports, and clear them on logout. `immediate` covers a session restored
  // from localStorage on reload.
  store.watch(
    (_state, getters) => getters.account.data,
    (user) => {
      Sentry.setUser(
        user
          ? {
              id: user.id,
              email: user.email,
              name: `${user.fname} ${user.lname}`,
              organizationId: user.organizationId,
              organizationName: user.organization?.name,
            }
          : null
      );
    },
    { immediate: true }
  );
};

// Record ids differ on every call; folding them keeps one broken endpoint to one issue.
const UUID_SEGMENT =
  /\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(?=\/|$)/gi;

/**
 * Reports a failed API call the backend could not report itself.
 *
 * A response in the backend's error envelope (`hasError`) is already in Sentry,
 * sent by the backend with the user attached. What is left is a request that got
 * no answer at all (offline, timed out, server down) or an error page from the
 * proxy in front of the server.
 */
export const captureApiError = (error: any) => {
  if (error?.response?.data?.hasError) return;

  const method = (error?.config?.method || 'get').toUpperCase();
  const url: string = error?.config?.url || '';
  const route = url.split('?')[0].replace(UUID_SEGMENT, '/:id');
  const outcome = String(error?.response?.status || error?.code || 'NO_RESPONSE');

  Sentry.withScope((scope: any) => {
    scope.setTag('api_route', `${method} ${route}`);
    scope.setTag('api_outcome', outcome);
    scope.setFingerprint(['api-error', method, route, outcome]);
    Sentry.captureException(error);
  });
};
